import { Box, Drawer, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { RxCross2 } from 'react-icons/rx';
import dayjs from 'dayjs';
import DataTable from 'components/Common/DataTable';
import { BusinessListAddon, BusinessProfileResponse } from 'apis/business/types';
import { getCancellationReasonLabel } from '@/constants/cancellationReasons';
import PaymentCancelTooltipIcon from '@/assets/icons/PaymentCancelTooltipIcon';

interface Props {
  open: boolean;
  business: BusinessProfileResponse | null;
  onClose: () => void;
}

function getAddonStatusStyle(theme: any, addon: BusinessListAddon) {
  const status = String(addon.status ?? '').toLowerCase();

  switch (status) {
    case 'active':
      return {
        label: 'Active',
        color: theme.palette.success.main,
        bg: theme.palette.success.light
      };
    case 'pending_cancellation':
    case 'cancelled':
      return {
        label: 'Inactive',
        color: theme.palette.error.main,
        bg: theme.palette.error.light
      };
    case 'pending_payment':
      return {
        label: 'Pending Payment',
        color: theme.palette.text.secondary,
        bg: '#F3F4F6'
      };
    default:
      return {
        label: addon.status || 'Unknown',
        color: theme.palette.text.secondary,
        bg: '#F3F4F6'
      };
  }
}

function buildInactiveAddonTooltipContent(addon: BusinessListAddon) {
  const deactivatedOn = addon.cancel_at ? dayjs(addon.cancel_at).format('DD MMM YYYY') : null;
  const reasonLabel = getCancellationReasonLabel(addon.cancellation_reason);

  return (
    <Box
      fontSize={12}
      component='ul'
      sx={{ m: 0, pl: 2, textAlign: 'left', '& li': { mb: 0.5, '&:last-of-type': { mb: 0 } } }}
    >
      {deactivatedOn ? <Box component='li'>Deactivated On - {deactivatedOn}</Box> : null}
      {reasonLabel ? <Box component='li'>{reasonLabel}</Box> : null}
      {addon.cancellation_feedback ? <Box component='li'>{addon.cancellation_feedback}</Box> : null}
    </Box>
  );
}

const inactiveAddonTooltipSlotProps = {
  tooltip: {
    sx: {
      bgcolor: 'black',
      color: 'white',
      boxShadow: 3,
      fontSize: '0.85rem',
      borderRadius: 1,
      p: 1.25,
      lineHeight: 1.4,
      maxWidth: 320
    }
  },
  arrow: { sx: { color: 'black' } }
};

function AddonStatusCell({ addon }: { addon: BusinessListAddon }) {
  const theme = useTheme();
  const style = getAddonStatusStyle(theme, addon);
  const status = String(addon.status ?? '').toLowerCase();
  const showCancelTooltip =
    (status === 'cancelled' || status === 'pending_cancellation') &&
    Boolean(addon.cancel_at || addon.cancellation_reason || addon.cancellation_feedback);

  const pill = (
    <Typography
      noWrap
      sx={{
        color: style.color,
        bgcolor: style.bg,
        borderRadius: '100px',
        padding: { xs: '4px 8px', sm: '4px 13px' },
        width: 'fit-content',
        fontSize: { xs: '11px', sm: '12px' },
        fontWeight: 500
      }}
    >
      {style.label}
    </Typography>
  );

  if (!showCancelTooltip) return pill;

  return (
    <Box display='flex' alignItems='center' gap={1}>
      {pill}
      <Tooltip
        title={buildInactiveAddonTooltipContent(addon)}
        placement='left'
        arrow
        slotProps={inactiveAddonTooltipSlotProps}
      >
        <Box component='span' display='inline-flex' sx={{ cursor: 'pointer', lineHeight: 0 }}>
          <PaymentCancelTooltipIcon />
        </Box>
      </Tooltip>
    </Box>
  );
}

const columns: ColumnDef<BusinessListAddon>[] = [
  {
    header: 'ADD-ON NAME',
    accessorKey: 'name',
    cell: (info) => (
      <Typography sx={{ fontSize: { xs: '14px', sm: '16px' } }}>{(info.getValue() as string) || '—'}</Typography>
    )
  },
  {
    header: 'ACTIVATED ON',
    accessorKey: 'activated_at',
    cell: (info) => {
      const date = info.getValue() as string | null;
      return (
        <Typography sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
          {date ? dayjs(date).format('MMM DD, YYYY') : '—'}
        </Typography>
      );
    }
  },
  {
    header: 'STATUS',
    accessorKey: 'status',
    cell: (info) => <AddonStatusCell addon={info.row.original} />
  }
];

export default function BusinessAddonsDrawer({ open, business, onClose }: Props) {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const addons = business?.business_addons ?? [];

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMediumScreen ? '100%' : 560,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        px={2.5}
        py={2}
        borderBottom='1px solid #ECECEC'
      >
        <Typography fontSize={20} fontWeight={500}>
          Add-Ons History
        </Typography>
        <IconButton size='small' onClick={onClose} aria-label='Close'>
          <RxCross2 />
        </IconButton>
      </Box>

      <Box flex={1} overflow='auto' px={2.5} py={2.5}>
        <DataTable data={addons} columns={columns} isHeaderSticky={false} />
      </Box>
    </Drawer>
  );
}
